import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Divider,
  FormControl, IconButton, InputLabel, Link, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { useNavigate, useParams } from "react-router-dom";
import { takedownApi } from "core/api";
import { TakedownAction, DeindexDossier, EmailCollectionStatus, DeindexSubmission } from "core/types/takedown.types";
import { categoryMeta } from "./replyCategories";

type TFn = (key: string, opts?: Record<string, unknown>) => string;

// Quante "Azioni richieste" restano sempre a schermo: le altre sono a discesa. Su una pratica
// reale sono decine (48 sulla 78) e mostrarle tutte rendeva il dettaglio impraticabile.
const REPLIES_PREVIEW = 3;

type ChannelMeta = { label: string; icon: React.ReactNode };
const CHANNEL_KEYS: Record<string, { key: string; icon: React.ReactNode }> = {
  email_diffida: { key: "takedown.detail.channel.email_diffida", icon: <GavelRoundedIcon fontSize="small" /> },
  cloudflare: { key: "takedown.detail.channel.cloudflare", icon: <CloudOutlinedIcon fontSize="small" /> },
  host_abuse: { key: "takedown.detail.channel.host_abuse", icon: <DnsOutlinedIcon fontSize="small" /> },
  registrar_abuse: { key: "takedown.detail.channel.registrar_abuse", icon: <LanguageOutlinedIcon fontSize="small" /> },
};

type ChipColor = "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";
const STATUS_KEYS: Record<string, { key: string; color: ChipColor }> = {
  prepared: { key: "takedown.detail.status.prepared", color: "warning" },
  approved: { key: "takedown.detail.status.approved", color: "info" },
  sending: { key: "takedown.detail.status.sending", color: "info" },
  sent: { key: "takedown.detail.status.sent", color: "primary" },
  delivered: { key: "takedown.detail.status.delivered", color: "success" },
  removed: { key: "takedown.detail.status.removed", color: "success" },
  superseded: { key: "takedown.detail.status.superseded", color: "success" },
  failed: { key: "takedown.detail.status.failed", color: "error" },
  deadletter: { key: "takedown.detail.status.deadletter", color: "error" },
  skipped: { key: "takedown.detail.status.skipped", color: "default" },
  rejected: { key: "takedown.detail.status.rejected", color: "default" },
};

// Stato ONLINE live del singolo link
const URL_STATUS_KEYS: Record<string, { key: string; color: ChipColor }> = {
  online: { key: "takedown.detail.url_status.online", color: "default" },
  pending: { key: "takedown.detail.url_status.pending", color: "warning" },
  removed: { key: "takedown.common.status.removed", color: "success" },
};

const CONTENT_TYPES = [
  { value: "general", labelKey: "takedown.detail.content_type.general" },
  { value: "copyright", labelKey: "takedown.detail.content_type.copyright" },
  { value: "ncii", labelKey: "takedown.detail.content_type.ncii" },
  { value: "defamation", labelKey: "takedown.detail.content_type.defamation" },
];

const statusMeta = (s: string, t: TFn) => {
  const m = STATUS_KEYS[s];
  return m ? { label: t(m.key), color: m.color } : { label: s, color: "default" as ChipColor };
};

// Esito ultima risposta provider (sottosistema B/inbox-processor)
const REPLY_STATE_KEYS: Record<string, { key: string; color: ChipColor }> = {
  provider_confirmed_removed: { key: "takedown.detail.reply_state.provider_confirmed_removed", color: "success" },
  acknowledged: { key: "takedown.detail.reply_state.acknowledged", color: "info" },
  forwarded_to_customer: { key: "takedown.detail.reply_state.forwarded_to_customer", color: "info" },
  redirected_offchannel: { key: "takedown.detail.reply_state.redirected_offchannel", color: "default" },
  needs_form_submission: { key: "takedown.detail.reply_state.needs_form_submission", color: "warning" },
  needs_info: { key: "takedown.detail.reply_state.needs_info", color: "warning" },
  auto_rerouted: { key: "takedown.detail.reply_state.auto_rerouted", color: "primary" },
  form_submitted: { key: "takedown.detail.reply_state.form_submitted", color: "primary" },
  rejected_by_provider: { key: "takedown.detail.reply_state.rejected_by_provider", color: "error" },
  counter_noticed: { key: "takedown.detail.reply_state.counter_noticed", color: "error" },
  site_responded: { key: "takedown.detail.reply_state.site_responded", color: "error" },
};
const replyStateMeta = (s: string | undefined | null, t: TFn) => {
  if (!s) return null;
  const m = REPLY_STATE_KEYS[s];
  return m ? { label: t(m.key), color: m.color } : { label: s, color: "default" as ChipColor };
};
const urlStatusMeta = (s: string | undefined | null, t: TFn) => {
  if (!s) return undefined;
  const m = URL_STATUS_KEYS[s];
  return m ? { label: t(m.key), color: m.color } : undefined;
};

// Icona di stato per il singolo link (rimosso / online / in attesa / sconosciuto)
const urlStatusIcon = (s: string | undefined | null, t: TFn): React.ReactNode => {
  switch (s) {
    case "removed":
      return (
        <Tooltip title={t("takedown.common.status.removed")}>
          <CheckCircleRoundedIcon fontSize="small" color="success" />
        </Tooltip>
      );
    case "online":
      return (
        <Tooltip title={t("takedown.detail.url_status_tooltip.online")}>
          <FiberManualRecordIcon fontSize="small" sx={{ color: "error.main" }} />
        </Tooltip>
      );
    case "pending":
      return (
        <Tooltip title={t("takedown.detail.url_status.pending")}>
          <HourglassEmptyRoundedIcon fontSize="small" color="warning" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title={t("takedown.detail.url_status_tooltip.unknown")}>
          <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
        </Tooltip>
      );
  }
};
const channelMeta = (c: string, t: TFn): ChannelMeta => {
  const m = CHANNEL_KEYS[c];
  return m ? { label: t(m.key), icon: m.icon } : { label: c, icon: <SendRoundedIcon fontSize="small" /> };
};

const recipientOf = (a: TakedownAction): string => {
  const p = a.prepared_payload || {};
  return p.recipient || p.report_type || p.endpoint || "—";
};

/** Approval gate: l'admin rivede cosa è stato PREPARATO e autorizza prima dell'invio in parallelo. */
export const TakedownPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { practiceId: practiceIdParam } = useParams<{ practiceId: string }>();
  const practiceId = practiceIdParam || "";
  const [contentType, setContentType] = useState<string>("general");
  const [actions, setActions] = useState<TakedownAction[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deindex, setDeindex] = useState<DeindexDossier[] | null>(null);
  // URL per cui la deindicizzazione e' l'UNICA strada (pagine di ricerca/tag): non hanno una
  // diffida, quindi se non si lavorano da qui non li lavora nessuno.
  const [deindexOnly, setDeindexOnly] = useState<string[]>([]);
  const [deindexSubs, setDeindexSubs] = useState<DeindexSubmission[]>([]);
  const [holderName, setHolderName] = useState("");          // nome titolare/cliente (campo Google)
  const [originalWorkUrl, setOriginalWorkUrl] = useState(""); // URL profilo/sito ufficiale (esempio opera)
  const [emailStatus, setEmailStatus] = useState<EmailCollectionStatus | null>(null); // raccolta email (finder) live
  const [replies, setReplies] = useState<any[]>([]);              // risposte provider che richiedono azione
  const [dossier, setDossier] = useState<any | null>(null);       // dossier form aperto (dialog)
  const [dossierFor, setDossierFor] = useState<any | null>(null); // reply per cui e' aperto il dossier
  const [showAllReplies, setShowAllReplies] = useState(false);    // "Azioni richieste": 3 + a discesa

  // Le critiche (counter-notice, rifiuti, minacce legali) salgono in cima: le 3 righe sempre
  // visibili devono essere le piu' urgenti, non le prime arrivate.
  const sortedReplies = useMemo(() => {
    const rank = (r: any) => (r?.severity === "critical" ? 0 : r?.severity === "action" ? 1 : 2);
    return [...replies].sort((a, b) => rank(a) - rank(b));
  }, [replies]);
  const criticalCount = useMemo(
    () => replies.filter((r: any) => r?.severity === "critical").length,
    [replies]
  );
  const visibleReplies = showAllReplies ? sortedReplies : sortedReplies.slice(0, REPLIES_PREVIEW);
  const hiddenReplies = Math.max(0, sortedReplies.length - REPLIES_PREVIEW);

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    setMsg(null);
    setSelected(new Set());   // evita id stantii in approve/reject dopo un "Ricarica" manuale
    try {
      setActions(await takedownApi.getActions(Number(practiceId)));
      try { setReplies((await takedownApi.getReplies(Number(practiceId))).replies || []); } catch { /* best-effort */ }
    } catch (e: any) {
      setMsg({ text: t("takedown.detail.load_error", { error: e?.message || e }), ok: false });
    } finally {
      setLoading(false);
    }
  }, [practiceId, t]);

  const openDossier = async (r: any) => {
    setDossierFor(r); setDossier(null);
    try {
      const d = await takedownApi.getFormDossier(r.action_id, r.provider || "");
      setDossier(d.dossier || { error: t("takedown.detail.dossier_unavailable") });
    } catch (e: any) {
      setDossier({ error: t("takedown.detail.dossier_error", { error: e?.message || e }) });
    }
  };
  const markSubmitted = async (r: any) => {
    const ref = window.prompt(t("takedown.detail.mark_submitted_prompt")) || undefined;
    try {
      await takedownApi.markFormSubmitted(r.action_id, ref);
      setMsg({ text: t("takedown.detail.mark_submitted_success"), ok: true });
      setDossier(null); setDossierFor(null);
      void load();
    } catch (e: any) {
      setMsg({ text: t("takedown.detail.generic_error", { error: e?.message || e }), ok: false });
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  // Stato LIVE della raccolta email (finder): polla mentre 'queued', si ferma a 'done'.
  const loadEmailStatus = useCallback(async () => {
    if (!practiceId) return;
    try {
      setEmailStatus(await takedownApi.getEmailStatus(Number(practiceId)));
    } catch {
      /* fail-silent: l'indicatore e' best-effort */
    }
  }, [practiceId]);

  useEffect(() => {
    void loadEmailStatus();
  }, [loadEmailStatus]);

  useEffect(() => {
    if (emailStatus?.status !== "queued") return; // auto-refresh solo finche' sta cercando
    const id = setInterval(() => void loadEmailStatus(), 4000);
    return () => clearInterval(id);
  }, [emailStatus?.status, loadEmailStatus]);

  // quando il finder finisce, ricarica le azioni cosi' compaiono i destinatari appena trovati
  useEffect(() => {
    if (emailStatus?.status === "done") void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailStatus?.status]);

  const run = async (fn: () => Promise<any>, ok: string) => {
    setLoading(true);
    setMsg(null);
    try {
      await fn();
      setMsg({ text: ok, ok: true });
      await load();
      setSelected(new Set());
    } catch (e: any) {
      setMsg({ text: t("takedown.detail.generic_error", { error: e?.message || e }), ok: false });
    } finally {
      setLoading(false);
    }
  };

  const prepare = () =>
    run(() => takedownApi.prepare(Number(practiceId), contentType), t("takedown.detail.prepare_success"));
  // Ripulisci e ri-prepara: cancella le azioni 'prepared' vecchie (la prepara normale e' sticky e non
  // rimuove cio' che il codice nuovo non produce piu') e rigenera. Approvate/inviate restano intatte.
  const reprepare = () => {
    if (!practiceId) return;
    if (!window.confirm(t("takedown.detail.reprepare_confirm"))) return;
    run(() => takedownApi.reprepare(Number(practiceId), contentType), t("takedown.detail.reprepare_success"));
    // refresh automatico (la prepare gira in bg): oltre al pulsante Ricarica manuale
    window.setTimeout(() => { void load(); }, 90000);
  };
  const approve = (ids: number[]) =>
    ids.length ? run(() => takedownApi.approve(ids), t("takedown.detail.approve_success", { count: ids.length })) : undefined;
  const reject = (ids: number[]) =>
    ids.length ? run(() => takedownApi.reject(ids), t("takedown.detail.reject_success", { count: ids.length })) : undefined;
  // M5: il backend risponde 200 anche su cap superato ({error}) o no-op ({sent_actions:0});
  // Axios non lancia -> bisogna ispezionare il body, altrimenti la UI mostrava un falso "Invio avviato".
  const send = async () => {
    if (!practiceId) return;
    setLoading(true);
    setMsg(null);
    try {
      const r = await takedownApi.send(Number(practiceId));
      const d: any = (r as any)?.data || {};
      if (d.error) {
        const extra = d.cap != null
          ? t("takedown.detail.send_error_extra", { used: d.used, cap: d.cap, requested: d.requested })
          : "";
        setMsg({ text: t("takedown.detail.send_error", { error: d.error, extra }), ok: false });
      } else if (d.status) {
        // invio ASINCRONO: il fan-out gira in background (throttle SMTP orario), la UI ricarica per lo stato
        setMsg({
          text: t("takedown.detail.send_async", { approved: d.approved, reserved: d.reserved }),
          ok: true,
        });
        window.setTimeout(() => { void load(); }, 30000);
      } else if ((d.sent_actions ?? 0) === 0) {
        setMsg({
          text: t("takedown.detail.send_zero", { note: d.note ? `: ${d.note}` : "" }),
          ok: false,
        });
      } else {
        setMsg({ text: t("takedown.detail.send_success", { count: d.sent_actions }), ok: true });
      }
      await load();
    } catch (e: any) {
      setMsg({ text: t("takedown.detail.send_catch_error", { error: e?.message || e }), ok: false });
    } finally {
      setLoading(false);
    }
  };

  const loadDeindex = async () => {
    if (!practiceId) return;
    setLoading(true);
    setMsg(null);
    try {
      const r = await takedownApi.getDeindex(
        Number(practiceId), contentType, true,
        holderName.trim() || undefined, originalWorkUrl.trim() || undefined);
      setDeindex(r.dossiers || []);
      setDeindexOnly(r.deindex_only || []);
      setDeindexSubs(r.submissions || []);
      if (!r.dossiers?.length) setMsg({ text: t("takedown.detail.deindex_empty"), ok: true });
    } catch (e: any) {
      setMsg({ text: t("takedown.detail.deindex_error", { error: e?.message || e }), ok: false });
    } finally {
      setLoading(false);
    }
  };

  /** Registra che la richiesta a quel motore e' partita. L'invio in se' resta manuale (i form dei
   *  motori hanno CAPTCHA): senza questa memoria nessuno sa piu' quali pratiche siano gia' fatte. */
  const markDeindexSent = async (engine: string, urlsCount: number) => {
    if (!practiceId) return;
    try {
      const r = await takedownApi.markDeindexSubmitted(Number(practiceId), engine, {
        urlsCount,
        contentType,
      });
      setDeindexSubs(r.submissions || []);
      setMsg({ text: t("takedown.detail.deindex_marked", { engine }), ok: true });
    } catch (e: any) {
      setMsg({ text: t("takedown.detail.deindex_mark_error", { error: e?.message || e }), ok: false });
    }
  };

  const copyText = useCallback(async (value: string, label = t("takedown.detail.copy_default_label")) => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard non disponibile");
      await navigator.clipboard.writeText(value);
      setMsg({ text: t("takedown.detail.copy_success", { label }), ok: true });
    } catch {
      setMsg({ text: t("takedown.detail.copy_error", { label: label.toLowerCase() }), ok: false });
    }
  }, [t]);

  const approvableIds = useMemo(
    () => actions.filter((a) => a.status === "prepared").map((a) => a.id),
    [actions]
  );

  // conteggio per stato (riepilogo in alto)
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    actions.forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [actions]);

  // Fase RAGGIUNTA dello stepper (0=Prepara 1=Approva 2=Invia 3=Riconcilia), derivata dallo stato reale.
  const reachedStep = useMemo(() => {
    if ((counts["removed"] || 0) > 0) return 3;
    if ((counts["sent"] || 0) + (counts["sending"] || 0) + (counts["delivered"] || 0) > 0) return 2;
    if ((counts["approved"] || 0) > 0) return 1;
    return 0;
  }, [counts]);

  // raggruppa per dominio (azioni per-dominio: ognuna copre tutti gli URL del dominio)
  const groups = useMemo(() => {
    const g: Record<string, TakedownAction[]> = {};
    actions.forEach((a) => {
      const key = String(a.domain_id ?? a.url_id ?? 0);
      (g[key] = g[key] || []).push(a);
    });
    return g;
  }, [actions]);

  const toggle = useCallback((id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    }), []);

  const copyRecipient = useCallback(async (value: string) => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard non disponibile");
      await navigator.clipboard.writeText(value);
      setMsg({ text: t("takedown.detail.copy_recipient_success", { value }), ok: true });
    } catch {
      setMsg({ text: t("takedown.detail.copy_recipient_error"), ok: false });
    }
  }, [t]);

  const hasActions = actions.length > 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {/* Torna alle pratiche */}
      <Link
        component="button"
        type="button"
        underline="hover"
        color="text.secondary"
        onClick={() => navigate("/web-rep/takedown")}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackRoundedIcon fontSize="small" />
        {t("takedown.detail.back_to_practices")}
      </Link>

      {/* Intestazione */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <GavelRoundedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {t("takedown.detail.title", { id: practiceId || "—" })}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("takedown.detail.desc_1")}<b>{t("takedown.detail.desc_bold1")}</b>{t("takedown.detail.desc_2")}
        <b>{t("takedown.detail.desc_bold2")}</b>{t("takedown.detail.desc_3")}<b>{t("takedown.detail.desc_bold3")}</b>{t("takedown.detail.desc_4")}
      </Typography>

      {/* Flusso — stepper dinamico: evidenzia le fasi raggiunte in base allo stato reale delle azioni */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        {[
          t("takedown.detail.step1"),
          t("takedown.detail.step2"),
          t("takedown.detail.step3"),
          t("takedown.detail.step4"),
        ].map((s, i) => (
          <Chip
            key={s}
            label={s}
            size="small"
            variant={i <= reachedStep ? "filled" : "outlined"}
            color={i <= reachedStep ? "primary" : "default"}
          />
        ))}
      </Stack>

      {/* Barra controlli */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="ct-label">{t("takedown.detail.content_type_label")}</InputLabel>
            <Select
              labelId="ct-label"
              label={t("takedown.detail.content_type_label")}
              value={contentType}
              onChange={(e) => setContentType(String(e.target.value))}
            >
              {CONTENT_TYPES.map((ct) => (
                <MenuItem key={ct.value} value={ct.value}>{t(ct.labelKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => void load()} disabled={!practiceId || loading}>
            {t("takedown.common.actions.reload")}
          </Button>
          <Button variant="contained" color="secondary" onClick={prepare} disabled={!practiceId || loading}>
            {t("takedown.detail.prepare_button")}
          </Button>
          <Tooltip title={t("takedown.detail.reprepare_tooltip")}>
            <span>
              <Button variant="outlined" color="warning" onClick={reprepare} disabled={!practiceId || loading}>
                {t("takedown.detail.reprepare_button")}
              </Button>
            </span>
          </Tooltip>
          {loading && <CircularProgress size={22} />}
        </Stack>
      </Paper>

      {msg && (
        <Alert severity={msg.ok ? "success" : "error"} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {/* AZIONI RICHIESTE — risposte dei provider che richiedono un intervento umano (cosa fare + perché).
          Se ne mostrano 3: su una pratica reale sono decine (48 sulla pratica 78) e aprire il
          dettaglio significava trovarsi una pagina che scorreva all'infinito prima di arrivare
          alle azioni vere. Le critiche vengono comunque prima, quindi le 3 visibili sono le piu'
          urgenti, non le prime capitate. */}
      {replies.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: "warning.main" }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("takedown.detail.actions_required_title", { count: replies.length })}
            </Typography>
            {criticalCount > 0 && (
              <Chip size="small" color="error" label={t("takedown.detail.actions_critical_count", { count: criticalCount })} />
            )}
          </Stack>
          <Stack spacing={1.5}>
            {visibleReplies.map((r) => {
              const crit = r.severity === "critical";
              const cm = categoryMeta(r.category, t);
              return (
                <Box key={r.id} sx={{ p: 1.5, border: "1px solid", borderColor: crit ? "error.light" : "warning.light", borderRadius: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={cm.color} label={cm.label} />
                    <Typography variant="body2" fontWeight={600}>
                      {r.provider || "—"}{r.domain ? ` · ${r.domain}` : ""}
                    </Typography>
                    {r.ticket_ref && <Chip size="small" variant="outlined" label={`ticket ${r.ticket_ref}`} />}
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5 }}><b>{t("takedown.detail.todo_label")}</b> {r.todo}</Typography>
                  {r.snippet && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {String(r.snippet).slice(0, 240)}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {r.category === "request_form" && r.action_id && (
                      <Button size="small" variant="contained" color="warning" onClick={() => openDossier(r)}>
                        {t("takedown.detail.open_form_dossier")}
                      </Button>
                    )}
                    {r.action_id && (
                      <Button size="small" variant="text" onClick={() => markSubmitted(r)}>{t("takedown.detail.mark_handled")}</Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          {hiddenReplies > 0 && (
            <Box sx={{ textAlign: "center", pt: 1.5 }}>
              <Button
                size="small"
                onClick={() => setShowAllReplies((v) => !v)}
                endIcon={showAllReplies ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              >
                {showAllReplies
                  ? t("takedown.detail.actions_collapse")
                  : t("takedown.detail.actions_show_more", { count: hiddenReplies })}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Dialog: dossier form pre-compilato (apri form provider + copia campi + segna inviato) */}
      <Dialog open={!!dossierFor} onClose={() => { setDossierFor(null); setDossier(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("takedown.detail.dossier_title", {
            provider: dossierFor?.provider,
            domain: dossierFor?.domain ? ` · ${dossierFor.domain}` : "",
          })}
        </DialogTitle>
        <DialogContent dividers>
          {!dossier && <CircularProgress size={24} />}
          {dossier?.error && <Alert severity="error">{dossier.error}</Alert>}
          {dossier && !dossier.error && (
            <Stack spacing={1.5}>
              <Button variant="contained" href={dossier.form_url} target="_blank" rel="noopener">
                {t("takedown.detail.open_provider_form")}
              </Button>
              {/* URL sempre visibile e copiabile: diversi form abuse stanno dietro WAF/anti-bot
                  (Cloudflare, Internet.bs, Dynadot) e il pulsante puo' finire su una pagina di
                  blocco o essere fermato dal browser. Con l'indirizzo a schermo l'operatore lo
                  incolla a mano e non resta bloccato. */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("takedown.detail.dossier_form_url_label")}
                </Typography>
                <Box
                  onClick={() => navigator.clipboard?.writeText(String(dossier.form_url || ""))}
                  sx={{ p: 1, bgcolor: "grey.100", borderRadius: 1, cursor: "pointer", fontSize: 13, wordBreak: "break-all" }}
                  title={t("takedown.detail.click_to_copy")}
                >
                  {dossier.form_url}
                </Box>
              </Box>
              {(dossier.captcha === "confirmed" || dossier.captcha === "possible" || dossier.automatable === "manual") && (
                <Alert severity="warning">
                  {dossier.captcha === "confirmed"
                    ? t("takedown.detail.dossier_captcha_confirmed")
                    : t("takedown.detail.dossier_captcha_possible")}
                </Alert>
              )}
              <Alert severity="info">
                {t("takedown.detail.dossier_instructions")}
              </Alert>
              {(dossier.form_fields || []).map((f: any, i: number) => (
                <Box key={i}>
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                  <Box
                    onClick={() => navigator.clipboard?.writeText(String(f.value || ""))}
                    sx={{ p: 1, bgcolor: "grey.100", borderRadius: 1, cursor: "pointer", whiteSpace: "pre-wrap", fontSize: 13 }}
                    title={t("takedown.detail.click_to_copy")}
                  >
                    {String(f.value || "")}
                  </Box>
                </Box>
              ))}
              {(dossier.checkboxes || []).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{t("takedown.detail.dossier_checkboxes_title")}</Typography>
                  <ul style={{ margin: "4px 0" }}>
                    {(dossier.checkboxes || []).map((c: string, i: number) => (
                      <li key={i} style={{ fontSize: 13 }}>{c}</li>
                    ))}
                  </ul>
                </Box>
              )}
              {dossier.note && <Alert severity="warning">{dossier.note}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDossierFor(null); setDossier(null); }}>{t("takedown.common.actions.close")}</Button>
          {dossierFor && (
            <Button variant="contained" color="success" onClick={() => markSubmitted(dossierFor)}>
              {t("takedown.detail.mark_submitted_button")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Raccolta email (finder) — indicatore LIVE: in corso / completata + X/Y trovate (auto-refresh) */}
      {emailStatus && emailStatus.domains_total > 0 && (
        <Alert
          severity={
            emailStatus.status === "queued"
              ? "info"
              : emailStatus.domains_with_email < emailStatus.domains_total
              ? "warning"
              : "success"
          }
          icon={emailStatus.status === "queued" ? <CircularProgress size={18} /> : undefined}
          sx={{ mb: 2 }}
        >
          {emailStatus.status === "queued" ? (
            <>
              {t("takedown.detail.email_collect_pre")}<b>{t("takedown.detail.email_collect_inprogress")}</b>{" "}
              <b>{emailStatus.domains_with_email}/{emailStatus.domains_total}</b> {t("takedown.detail.email_collect_found_suffix")}
            </>
          ) : (
            <>
              {t("takedown.detail.email_collect_done_pre")}<b>{t("takedown.detail.email_collect_done_bold")}</b>: <b>{emailStatus.domains_with_email}/{emailStatus.domains_total}</b> {t("takedown.detail.email_collect_done_suffix")}
              {emailStatus.domains_with_email < emailStatus.domains_total &&
                t("takedown.detail.email_collect_missing", { count: emailStatus.domains_total - emailStatus.domains_with_email })}
            </>
          )}
        </Alert>
      )}

      {hasActions && (
        <>
          {/* Riepilogo stati */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={t("takedown.detail.summary_chip", { actions: actions.length, domains: Object.keys(groups).length })} />
            {Object.keys(counts).map((s) => {
              const sm = statusMeta(s, t);
              return (
                <Chip key={s} size="small" color={sm.color} label={t("takedown.detail.status_count", { label: sm.label, count: counts[s] })} />
              );
            })}
          </Stack>

          {/* Azioni bulk */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, position: "sticky", top: 8, zIndex: 1, bgcolor: "background.paper" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={() => approve(approvableIds)} disabled={!approvableIds.length || loading}>
                {t("takedown.detail.approve_all_prepared", { count: approvableIds.length })}
              </Button>
              <Button variant="outlined" onClick={() => approve(Array.from(selected))} disabled={!selected.size || loading}>
                {t("takedown.detail.approve_selected", { count: selected.size })}
              </Button>
              <Button variant="outlined" color="error" onClick={() => reject(Array.from(selected))} disabled={!selected.size || loading}>
                {t("takedown.detail.reject_selected")}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                color="success"
                startIcon={<SendRoundedIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={loading || !(counts["approved"] > 0)}
              >
                {t("takedown.detail.send_approved")}
              </Button>
            </Stack>
          </Paper>
        </>
      )}

      {/* Stato vuoto */}
      {!hasActions && !loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}>
          <GavelRoundedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">
            {t("takedown.detail.empty_pre")}<b>{t("takedown.detail.empty_bold")}</b>.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {t("takedown.detail.empty_caption")}
          </Typography>
        </Paper>
      )}

      {/* Card per dominio */}
      {Object.entries(groups).map(([domId, acts]) => {
        const urls = acts[0]?.urls || [];
        return (
          <Card key={domId} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <DnsOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={600}>{t("takedown.detail.domain_title", { id: domId })}</Typography>
                <Chip size="small" variant="outlined" label={t("takedown.detail.url_count", { count: urls.length })} icon={<LinkOutlinedIcon />} />
              </Stack>

              {urls.length > 0 && (
                <Box sx={{ mb: 1.5, pl: 0.5 }}>
                  {urls.map((u, i) => {
                    const um = urlStatusMeta(u.status, t);
                    return (
                      <Stack
                        key={u.url_id ?? i}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        {urlStatusIcon(u.status, t)}
                        <Tooltip title={u.url || `URL #${u.url_id}`}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: "block", flex: 1, minWidth: 0 }}
                          >
                            {u.url || `URL #${u.url_id}`}
                          </Typography>
                        </Tooltip>
                        {um && (
                          <Chip
                            size="small"
                            variant={u.status === "online" ? "outlined" : "filled"}
                            color={um.color}
                            label={um.label}
                          />
                        )}
                      </Stack>
                    );
                  })}
                </Box>
              )}

              <Divider sx={{ mb: 1 }} />

              {acts.map((a) => {
                const cm = channelMeta(a.channel, t);
                const sm = statusMeta(a.status, t);
                const recipient = recipientOf(a);
                // copia solo per destinatari email reali (no report_type/endpoint Cloudflare)
                const hasRecipient = recipient !== "—" && /\S+@\S+/.test(recipient);
                return (
                  <Stack
                    key={a.id}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ py: 0.75, borderBottom: "1px dashed", borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
                  >
                    <Checkbox
                      size="small"
                      checked={selected.has(a.id)}
                      disabled={a.status !== "prepared"}
                      onChange={() => toggle(a.id)}
                    />
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: 180 }}>
                      {cm.icon}
                      <Typography variant="body2">{cm.label}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                      <Tooltip title={recipient}>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>{recipient}</Typography>
                      </Tooltip>
                      {hasRecipient && (
                        <Tooltip title={t("takedown.common.actions.copy")}>
                          <IconButton size="small" onClick={() => copyRecipient(recipient)}>
                            <ContentCopyRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {a.attempts > 0 && (
                      <Tooltip title={t("takedown.detail.attempts_tooltip")}>
                        <Chip size="small" variant="outlined" label={`×${a.attempts}`} />
                      </Tooltip>
                    )}
                    <Chip size="small" color={sm.color} label={sm.label} />
                    {a.approval_mode && (
                      <Tooltip
                        title={
                          a.approval_mode === "auto"
                            ? t("takedown.detail.approval_mode_auto_tooltip")
                            : t("takedown.detail.approval_mode_manual_tooltip")
                        }
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color={a.approval_mode === "auto" ? "success" : "default"}
                          label={a.approval_mode === "auto" ? t("takedown.detail.approval_auto") : t("takedown.detail.approval_manual")}
                        />
                      </Tooltip>
                    )}
                    {a.legal_review && (
                      <Tooltip title={t("takedown.detail.legal_review_tooltip")}>
                        <Chip size="small" variant="outlined" color="warning" label={t("takedown.detail.legal_review_label")} />
                      </Tooltip>
                    )}
                    {(() => {
                      const rsm = replyStateMeta(a.reply_state, t);
                      if (!rsm) return null;
                      return (
                        <Tooltip
                          title={
                            a.provider_case_ref
                              ? t("takedown.detail.provider_case_ref_tooltip", { ref: a.provider_case_ref })
                              : t("takedown.detail.reply_state_tooltip_default")
                          }
                        >
                          <Chip size="small" variant="outlined" color={rsm.color} label={rsm.label} />
                        </Tooltip>
                      );
                    })()}
                    {a.approved_by && (
                      <Tooltip title={t("takedown.detail.approved_by_tooltip", { name: a.approved_by })}>
                        <Typography variant="caption" color="text.secondary">✔</Typography>
                      </Tooltip>
                    )}
                    {a.last_error && (
                      <Tooltip title={a.last_error}>
                        <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 160 }}>
                          {a.last_error}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}
            </CardContent>
          </Card>
        );
      })}

      {/* Legenda canali */}
      {hasActions && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2, bgcolor: "action.hover" }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{t("takedown.detail.channels_label")}</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {Object.keys(CHANNEL_KEYS).map((c) => (
              <Stack key={c} direction="row" spacing={0.5} alignItems="center">
                {CHANNEL_KEYS[c].icon}
                <Typography variant="caption" color="text.secondary">{t(CHANNEL_KEYS[c].key)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Deindex dai motori (auto-fill + 1 click) */}
      {hasActions && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TravelExploreRoundedIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t("takedown.detail.deindex_title")}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("takedown.detail.deindex_desc_pre")}
            <b>{t("takedown.detail.deindex_desc_bold")}</b>{t("takedown.detail.deindex_desc_post")}
          </Typography>
          {/* Campi case-specific del form Google DMCA: titolare (cliente) + esempio opera */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              label={t("takedown.detail.holder_name_label")}
              placeholder={t("takedown.detail.holder_name_placeholder")}
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
            />
            <TextField
              size="small"
              fullWidth
              label={t("takedown.detail.original_work_label")}
              placeholder={t("takedown.detail.original_work_placeholder")}
              value={originalWorkUrl}
              onChange={(e) => setOriginalWorkUrl(e.target.value)}
            />
          </Stack>
          <Button variant="outlined" onClick={loadDeindex} disabled={!practiceId || loading}>
            {t("takedown.detail.generate_deindex_button")}
          </Button>

          {/* Le pagine di ricerca/tag non sono rimuovibili dall'host: non hanno una diffida e
              questa e' l'unica schermata dove qualcuno se ne occupa. Se non lo si dice, restano
              indietro per sempre — sullo storico erano 165 URL mai lavorati. */}
          {deindexOnly.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {t("takedown.detail.deindex_only_title", { count: deindexOnly.length })}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("takedown.detail.deindex_only_desc")}
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyRoundedIcon />}
                onClick={() => copyText(deindexOnly.join("\n"), t("takedown.detail.deindex_only_copy_label"))}
              >
                {t("takedown.detail.deindex_only_copy", { count: deindexOnly.length })}
              </Button>
            </Alert>
          )}

          {deindex?.map((d, i) => (
            <Paper key={d.engine || i} variant="outlined" sx={{ p: 1.5, mt: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                  {d.title}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<OpenInNewRoundedIcon />}
                  component="a"
                  href={d.form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("takedown.detail.open_form_button")}
                </Button>
                {!d.form_fields && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyRoundedIcon />}
                    onClick={() => copyText(d.paste, t("takedown.detail.copy_default_label"))}
                  >
                    {t("takedown.detail.copy_text_button")}
                  </Button>
                )}
                {/* Memoria dell'invio: i form dei motori hanno CAPTCHA e restano manuali, quindi
                    l'unico modo per sapere cosa e' gia' partito e' che l'operatore lo segni. */}
                {(() => {
                  const done = deindexSubs.find((s) => s.engine === d.engine);
                  return done ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleRoundedIcon />}
                      label={t("takedown.detail.deindex_sent_on", {
                        date: new Date(done.submitted_at).toLocaleDateString(),
                      })}
                      onClick={() => markDeindexSent(d.engine, d.urls?.length || 0)}
                      title={t("takedown.detail.deindex_resend_hint")}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => markDeindexSent(d.engine, d.urls?.length || 0)}
                    >
                      {t("takedown.detail.deindex_mark_sent")}
                    </Button>
                  );
                })()}
              </Stack>
              {d.when && (
                <Typography variant="caption" color="text.secondary">{d.when}</Typography>
              )}
              {/* Form a CAMPI discreti (Google DMCA Web Search): 1 "Copia" per campo */}
              {d.form_fields && d.form_fields.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {d.form_fields.map((f) => (
                    <Stack
                      key={f.label}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ py: 0.5, borderBottom: "1px dashed", borderColor: "divider" }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ width: 230, flexShrink: 0, pt: 0.5 }}>
                        {f.label}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {f.value || "—"}
                      </Typography>
                      <Tooltip title={t("takedown.common.actions.copy")}>
                        <span>
                          <IconButton size="small" onClick={() => copyText(f.value, f.label)} disabled={!f.value}>
                            <ContentCopyRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}
                  {d.checkboxes && d.checkboxes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t("takedown.detail.deindex_checkboxes_title")}
                      </Typography>
                      {d.checkboxes.map((c, k) => (
                        <Typography key={k} variant="caption" color="text.secondary" sx={{ display: "block", pl: 1 }}>
                          ☐ {c}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          ))}
        </Paper>
      )}

      {/* Conferma invio fan-out reale */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("takedown.detail.confirm_title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("takedown.detail.confirm_text", { count: counts["approved"] || 0 })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>{t("takedown.common.actions.cancel")}</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SendRoundedIcon />}
            onClick={() => {
              setConfirmOpen(false);
              send();
            }}
            disabled={loading}
          >
            {t("takedown.detail.confirm_send_button")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TakedownPage;
